/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { BaseGuildView } from './base-guild-view.js';
import { guildManageViewStyles } from './guild-manage-view-styles.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';
import { TabRouteMixin } from '../../modules/tab-route-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/breadcrumb-item/breadcrumb-item.js';
import '@shoelace-style/shoelace/dist/components/breadcrumb/breadcrumb.js';
import '@shoelace-style/shoelace/dist/components/color-picker/color-picker.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/range/range.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-alert-bar/q1-alert-bar.js';
import '../../components/q1-guild-activation-panel/q1-guild-activation-panel.js';
import '../../components/q1-guild-card/q1-guild-card.js';
import '../../components/q1-md-textarea/q1-md-textarea.js';
import '../../components/q1-member-chip/q1-member-chip.js';
import '../../components/q1-membership-list/q1-membership-list.js';
import '../../components/q1-external-resource-list/q1-external-resource-list.js';

/*=== Q1 MODULES ===*/
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';
import { enrichGuild } from '../../modules/guild-helpers-client.js';
import { q1DocFunctions, q1ExternalResourceFunctions } from '../../modules/q1-core-functions.js';

/*=== CONSTANTS ===*/

const ALL_MEMBERS_SEARCH_CACHE = 'all-members-search';

const CACHE_MAX_AGE = 5; // in minutes

// Anytime these fields are updated, we need to update the guild styles.
const WATCHED_THEME_FIELDS = new Set([
  'colorPrimary', 'colorSecondary',
  'overlayOpacity', 'bannerOpacity',
  'textColorPrimary', 'textColorSecondary',
]);

import {
  guildTextColorOptions,
  guildVisibilityOptions,
  guildJoinabilityOptions,
} from '../../models/guild-client-enums.js'

const TAB_SETTINGS = (guild) => ({
  members: {
    path: `/${guild.key}/manage`,
    title: `Manage Members: ${guild.name} - q1 Network`,
  },
  projects: {
    path: `/${guild.key}/manage/projects`,
    title: `Manage Projects: ${guild.name} - q1 Network`,
  },
  details: {
    path: `/${guild.key}/manage/details`,
    title: `Manage Details: ${guild.name} - q1 Network`,
  },
  theme: {
    path: `/${guild.key}/manage/theme`,
    title: `Manage Theme: ${guild.name} - q1 Network`,
  },
  settings: {
    path: `/${guild.key}/manage/settings`,
    title: `Manage Settings: ${guild.name} - q1 Network`,
  },
});

/*=== VIEW CLASS DEFINITION ===*/

export class GuildManageView extends FormHostMixin(TabRouteMixin(BaseGuildView)) {
  static styles = [
    ...super.styles,
    guildManageViewStyles,
  ];

  static properties = {
    ...super.properties,

    name: { type: String },
    headline: { type: String },
    description: { type: String },
    colorPrimary: { type: String },
    visibility: { type: String },
    memberListVisibility: { type: String },
    memberProfileVisibility: { type: String },
    projectListVisibility: { type: String },
    projectProfileVisibility: { type: String },
    supporterJoinability: { type: String },
    contributorJoinability: { type: String },
    projectJoinability: { type: String },
    avatar: { type: Object },
    banner: { type: Object },
    avatar_newUploadPath: { type: String },
    banner_newUploadPath: { type: String },
    bannerOpacity: { type: Number },
    overlayOpacity: { type: Number },
    textColorPrimary: { type: String },
    textColorSecondary: { type: String },
    colorSecondary: { type: String },
    externalResources: { type: Array },

    _allMembersByRef: { type: Object },
    
    rangeTooltipFormatter: { type: Object },

    newPoint_processing: { type: Boolean },
  };

  constructor() {
    super();

    this.name = null;
    this.headline = null;
    this.description = null;
    this.colorPrimary = null;
    this.visibility = null;
    this.memberListVisibility = null;
    this.memberProfileVisibility = null;
    this.projectListVisibility = null;
    this.projectProfileVisibility = null;
    this.supporterJoinability = null;
    this.contributorJoinability = null;
    this.projectJoinability = null;
    this.avatar = null;
    this.banner = null;
    this.avatar_newUploadPath = null;
    this.banner_newUploadPath = null;
    this.bannerOpacity = null;
    this.overlayOpacity = null;
    this.textColorPrimary = null;
    this.textColorSecondary = null;
    this.colorSecondary = null;
    this.externalResources = null;

    this._allMembersByRef = {};

    this.rangeTooltipFormatter = (value) => `${Math.round(value * 100)}%`;

    this.newPoint_processing = false;
  }

  /*=== LIFECYCLE ===*/
  
  connectedCallback() {
    super.connectedCallback();
  }

  update(changedProperties) {
    super.update(changedProperties);

    if (changedProperties.has('guild')) {
      this.name = this.guild?.name;
      this.headline = this.guild?.headline;
      this.description = this.guild?.description;
      this.colorPrimary = this.guild?.colorPrimary;
      this.visibility = this.guild?.visibility;
      this.memberListVisibility = this.guild?.memberListVisibility;
      this.memberProfileVisibility = this.guild?.memberProfileVisibility;
      this.projectListVisibility = this.guild?.projectListVisibility;
      this.projectProfileVisibility = this.guild?.projectProfileVisibility;
      this.supporterJoinability = this.guild?.supporterJoinability;
      this.contributorJoinability = this.guild?.contributorJoinability;
      this.projectJoinability = this.guild?.projectJoinability;
      this.avatar = this.guild?.avatar;
      this.banner = this.guild?.banner;
      this.bannerOpacity = this.guild?.bannerOpacity;
      this.overlayOpacity = this.guild?.overlayOpacity;
      this.textColorPrimary = this.guild?.textColorPrimary;
      this.textColorSecondary = this.guild?.textColorSecondary;
      this.colorSecondary = this.guild?.colorSecondary;
    }

    if (WATCHED_THEME_FIELDS.intersection(changedProperties).size) {
      const guildCardPreviewElement = this.renderRoot.getElementById('guild-card-preview');
      
      if (guildCardPreviewElement) {
        // Mock up a version of the guild with the updated theme fields
        const mockGuild = enrichGuild({
          ...this.guild,
          ...Object.fromEntries(
            Array.from(WATCHED_THEME_FIELDS).map(field => [field, this[field]])
          ),
        })

        // Then pass that as the source object to set guild styles on the guild card preview
        this._setGuildStyles(mockGuild, guildCardPreviewElement);
        this._setGuildStyles(mockGuild, this);

        // Finally, lets toggle the app header white text as needed
        this.renderRoot.querySelector('q1-app-header').variant = mockGuild.textColorPrimary_isWhite
          ? 'white-text'
          : 'default';
      }
    }
  }

  /** Overwriting from parent. Detect additional error states related to current member role. */
  async guildLoaded() {
    if (!['organizer', 'point'].includes(this.context?.role)) {
      this._loadError_redirectToDetailView('You do not have permission to manage this guild.');
    }

    let allMembersSearchCache = 
      await this.appElement.cacheManager.getCache(ALL_MEMBERS_SEARCH_CACHE, CACHE_MAX_AGE);
    if (allMembersSearchCache.isNewOrStale) {
      allMembersSearchCache = await allMembersSearchCache.refreshComplete;
    }

    // Build a map of all members to provide to the membership enrichment method
    this._allMembersByRef = buildRefPathMap(allMembersSearchCache?.data, 'members', 'members');

    await this.refreshExternalResources();
  }

  /*=== ACTIONS ===*/

  async refreshExternalResources() {
    const { browseExternalResources } = q1ExternalResourceFunctions;

    try {
      const response = await browseExternalResources({
        subjectId: this.guild.id,
        subjectCollection: 'guilds',
      });
      this.externalResources  = response.data.externalResources || [];
    } catch (err) {
      console.error('Failed to fetch external resources:', err);
    }
  }

  updateGuild(event) {
    const targetElement = event.target;
    const targetForm = targetElement.closest('form');
    if (!targetForm) {
      console.error('No form found for target element:', targetElement);
      return;
    }
    
    if (!targetForm.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      return;
    }
    
    const { updateDoc } = q1DocFunctions;
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
    this.error = false;
    this.errorMessage = '';
    this.scrollToCenterOf('view-content');

    // Serialize the form data and clean up the number fields
    const formData = this._serializeForm(targetForm);
    if (formData.overlayOpacity) formData.overlayOpacity = parseFloat(formData.overlayOpacity);
    if (formData.bannerOpacity) formData.bannerOpacity = parseFloat(formData.bannerOpacity);
    
    updateDoc({
      docCollection: 'guilds',
      docId: this.guild.id,
      docUpdate: formData,
    }).then((response) => {
      this.showToast(null, 'Guild updated successfully!', 'success');
      this._refreshGuild(); // this will clear this.loading when done
    }).catch((error) => {
      console.error('Error calling function:', error);
      this.showToast(error.message, 'There was an error updating the guild!', 'danger');
      this.errorMessage = error.message;
      this.loading = false;
    }).finally(() => {
      this.scrollToTop();
    });
  }

  showNewPointDialog() {
    this.renderRoot.getElementById('new-point-dialog').show();
  }

  closeNewPointDialog() {
    this.renderRoot.getElementById('new-point-dialog').hide();
  }

  async saveNewPoint() {
    if (this.newPoint_processing) return;

    const newPointForm = this.renderRoot.getElementById('new-point-form');
    if (!newPointForm.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      return;
    }
    
    const { updateDoc } = q1DocFunctions;
    this.newPoint_processing = true;
    const newPoint = this.renderRoot.getElementById('new-point-select').value;

    try {
      const response = await updateDoc({
        docCollection: 'guilds',
        docId: this.guild.id,
        docUpdate: { newPoint },
      });

      this.showToast(
        'The transition might take a few minutes to complete.',
        'New guild point assigned successfully!', 'success',
        10000
      );

      // Wait 2 seconds then reload the guild
      await new Promise(resolve => setTimeout(resolve, 2000));
      this._refreshGuild();

      this.closeNewPointDialog();
    } catch (error) {
      console.error('Error updating guild:', error);
      this.showToast(error.message, 'Error assigning new guild point!', 'danger');
      return;
    } finally {
      this.newPoint_processing = false;
    }
  }

  showActivationPanel() {
    const activationPanel = this.shadowRoot.getElementById('activation-panel');
    if (!this.canManageGuild || !activationPanel) return;
    activationPanel.show();
  }

  async publish() {
    if (!this.canPublish) return;
    if (!this.guild.headline || !this.guild.description) {
      this.showToast('Please add a headline and description before publishing.', null, 'danger');
      return;
    }
    if (!confirm('Ready to publish your guild?')) return;

    await this._setGuildStatus('live', 'publishing', 'published');
  }

  async archive() {
    if (!this.canArchive) return;
    if (!confirm('Are you sure you want to archive this guild?')) return;

    await this._setGuildStatus('archived', 'archiving', 'archived');
  }

  async unarchive() {
    if (!this.canUnarchive) return;
    if (!confirm('Are you sure you want to unarchive this guild?')) return;

    await this._setGuildStatus('live', 'unarchiving', 'unarchived');
  }

  /**
   * @param {string} status - The value of `guild.status` to set
   * @param {string} w_updating - The word to use: 'There was an error [updating] the guild'
   * @param {string} w_updated - The word to use: 'Guild [updated] successfully'
   */
  async _setGuildStatus(status, w_updating, w_updated) {
    if (!this.isPoint || this.isLoading) return;

    const { updateDoc } = q1DocFunctions;
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
    this.scrollToCenterOf('view-content');

    try {
      const response = await updateDoc({
        docCollection: 'guilds',
        docId: this.guild.id,
        docUpdate: { status },
      });

      this.showToast(null, `Guild ${w_updated} successfully!`, 'success');
      
      // Wait 1 second then reload the guild
      await new Promise(resolve => setTimeout(resolve, 1000));
      this._refreshGuild();
    } catch (error) {
      console.error('Error updating guild:', error);
      this.showToast(error.message, `There was an error ${w_updating} the guild!`, 'danger');
      this.error = true;
      this.errorMessage = error.message;
    } finally {
      this.loading = false;
      this.scrollToTop();
    }
  }

  /*=== TAB ROUTE IMPLEMENTATION METHODS ===*/

  _getTabPath(tabKey) {
    if (!this.guild) return window.location.pathname;

    return TAB_SETTINGS(this.guild)[tabKey]?.path || TAB_SETTINGS(this.guild).members.path;
  }

  _getTabTitle(tabKey) {
    if (!this.guild) return 'Guild Manage View - q1 Network';

    return TAB_SETTINGS(this.guild)[tabKey]?.title || 'Guild Manage View - q1 Network';
  }

  /*=== HANDLERS ===*/

  handleGuildFormSubmit(event) {
    // IMPROVEME: We're getting an "invalid form control... not focusable" error when the create
    // guild logic is run in the context of a form submit rather than a button click.
    // Not sure how to resolve this. THIS ISN'T RUNNING RIGHT NOW. Leaving it here for now.

    event.preventDefault();
  }

  /*=== GETTERS ===*/

  get hasGuildProjects(){
    return this.currentMember?.flags?.includes('gp-testing') || false;
  }

  get isPoint() {
    return this.context?.role === 'point';
  }

  get currentPoint() {
    if (!this.guild?.point) return null;

    return this._allMembersByRef[this.guild.point];
  }

  get newPoint() {
    if (!this.guild?.newPoint) return null;

    return this._allMembersByRef[this.guild.newPoint];
  }

  get organizerOptions() {
    if (!this.guild?.organizers) return [];

    return this.guild.organizers.map((organizerPath) => {
      const member = this._allMembersByRef[organizerPath];
      const isSelf = this.currentMember.id === member.id;

      return {
        value: organizerPath,
        label: member.nameInternal,
        isSelf,
      };
    });
  }

  get canPublish() {
    return this.context?.allowedActions?.includes?.('publish') || false;
  }

  get canArchive() {
    return this.context?.allowedActions?.includes?.('archive') || false;
  }

  get canUnarchive() {
    return this.context?.allowedActions?.includes?.('unarchive') || false;
  }

  get isActivationPanelAvailable() {
    return this.canManageGuild &&
      ['created', 'published', 'seeded', 'activated'].includes(this.guild?.activationStatus);
  }
  
  get isActivationBarOpen() {
    return this.isActivationPanelAvailable; // these are the same in the manage view
  }
  
  /*=== TEMPLATES ===*/

  get contentHeaderTemplate() {
    if (['init', 'loaded'].includes(this.viewMode)) return null;

    return html`
      <header-bar class="with-border">
        <h1>
          <span class="prefix">q1 Network Guild</span>
          Guild Key: ${this.guildKey || '[ None ]'}
        </h1>
      </header-bar>
    `;
  }

  get viewModeTemplate_loaded() {
    return html`
      ${this.activationBarTemplate}

      ${this.loadedHeaderTemplate}

      <sl-tab-group
        id="guild-tabs"
        @sl-tab-show=${this.handleTabShow}
        data-tab-route="source"
      >
        <sl-tab slot="nav" panel="members">
          Members
        </sl-tab>
        ${!this.hasGuildProjects ? null : html`
        <sl-tab slot="nav" panel="projects">
          Projects
        </sl-tab>
        `}
        <sl-tab slot="nav" panel="details">
          Details
        </sl-tab>
        <sl-tab slot="nav" panel="theme">
          Theme
        </sl-tab>
        <sl-tab slot="nav" panel="settings">
          Settings
        </sl-tab>

        <sl-divider class="full-width"></sl-divider>

        ${this.loadedBodyContentTemplate}
      </sl-tab-group>

      ${this.newPointDialogTemplate}

      ${this.activationPanelTemplate}
    `;
  }

  get loadedHeaderTemplate() {
    return html`
      <guild-header>
        <guild-header-content>
          <sl-avatar
            id="guild-avatar"
            label="${this.guild.name}"
            image=${this.guild.avatar?.standard?.url}
          >
            <sl-icon slot="icon" library="material" name="hive"></sl-icon>
          </sl-avatar>
          <header-bar class="nested">
            <header-content>
              <sl-breadcrumb>
                <sl-breadcrumb-item href="/${this.guildKey}">${this.guild.name}</sl-breadcrumb-item>
                <sl-breadcrumb-item>Manage Guild</sl-breadcrumb-item>
              </sl-breadcrumb>
  
              <h1>
                Guild Management & Settings
              </h1>
            </header-content>
          </header-bar>
        </guild-header-content>
      </guild-header>
    `;
  }

  get activationBarTemplate() {
    if (!this.isActivationBarOpen) return null;

    return html`
      <q1-alert-bar
        id="activation-bar"
        message="Continue activating your guild..."
        @q1-click=${this.showActivationPanel}
        icon="brightness_alert"
        btn-label="Next Steps"
        btn-icon="arrow_forward"
        variant="light"
        class="sl-themed"
      ></q1-alert-bar>
    `;
  }

  get activationPanelTemplate() {
    if (!this.isActivationPanelAvailable) return null;

    return html`
      <q1-guild-activation-panel
        id="activation-panel"
        .guild=${this.guild}
        class="sl-themed"
      ></q1-guild-activation-panel>
    `;
  }

  get loadedBodyContentTemplate() {
    return html`
      <sl-tab-panel name="members">
        ${this.membersTabTemplate}
      </sl-tab-panel>
      <sl-tab-panel name="projects">
        ${this.projectsTabTemplate}
      </sl-tab-panel>
      <sl-tab-panel name="details">
        ${this.detailsTabTemplate}
      </sl-tab-panel>
      <sl-tab-panel name="theme">
        ${this.themeTabTemplate}
      </sl-tab-panel>
      <sl-tab-panel name="settings">
        ${this.settingsTabTemplate}
      </sl-tab-panel>
    `;
  }

  get membersTabTemplate() {
    return html`
      <no-column-wrapper class="sl-themed-secondary">
        <header-bar class="nested">
          <h3>Guild Members</h3>

          <button-bar class="align-center">
            <sl-button
              variant="primary"
              href="/${this.guildKey}/invite"
              pill
            >
              <sl-icon slot="prefix" library="material" name="person_add"></sl-icon>
              Invite
            </sl-button>
          </button-bar>
        </header-bar>

        <q1-membership-list
          subject=${this.guild.ref}
          subject-detail-view-path=${this.guild.detailViewPath}
          query="guildMembers"
          .memberRefMap=${this._allMembersByRef}
          status="all"
          signed-in
          enable-status-filter
          class="full-width"
        ></q1-membership-list>
      </no-column-wrapper>
    `;
  }

  get projectsTabTemplate() {
    return html`
      <no-column-wrapper>
        <h3>Guild Projects</h3>
        
        <!-- ${this.detailsFormTemplate} -->
        <!-- FIXME: This is a placeholder for the projects tab. -->

        <q1-spinner-panel label="Processing..." ?open=${this.loading}></q1-spinner-panel>
      </no-column-wrapper>
    `;
  }

  get detailsTabTemplate() {
    return html`
      <no-column-wrapper>
        <h3>Guild Narrative</h3>
        
        ${this.detailsFormTemplate}

        <sl-divider></sl-divider>
        
        <h2>Guild Resources</h2>
        
        ${this.resourcesFormTemplate}

        <q1-spinner-panel label="Processing..." ?open=${this.loading}></q1-spinner-panel>
      </no-column-wrapper>
    `;
  }

  get themeTabTemplate() {
    return html`
      <no-column-wrapper>
        <h3>Edit Guild Theme</h3>

        ${this.themeFormTemplate}
      </no-column-wrapper>
    `;
  }

  get settingsTabTemplate() {
    return html`
      <no-column-wrapper class="sl-themed-secondary">
        <sl-alert variant="neutral" open>
          <sl-icon library="material" name="info" slot="icon"></sl-icon>
          <strong>Permissions Note:</strong> Everything in this settings 
          screen is visible to all organizers, but editable only to the 
          guild point.
        </sl-alert>

        ${this.settingsFormTemplate}

        <sl-divider></sl-divider>

        <h2>Guild Key</h2>

        <p>
          The guild key sets the URL of your guild as well as other resources.
          Mostly Slack channel names for now.
        </p>

        <p>
          <strong>Guild Key Editing Notes:</strong> Only available for activated guilds.
          Not implemented yet. Coming soon.
        </p>

        <table class="utility-table">
          <tr>
            <td>Guild Key:</td>
            <td class="align-right">
              <sl-input
                id="guild-key"
                value=${this.guild.key}
                readonly disabled
              ></sl-input>
            </td>
          </tr>

          <tr>
            <td>Current Guild URL:</td>
            <td class="align-right">
              <a
                id="current-guild-url"
                href=${this.guildUrl}
                target="_blank"
              >${this.guildUrl}</a>
              <sl-copy-button from="current-guild-url.href"></sl-copy-button>
            </td>
          </tr>
        </table>

        <sl-divider></sl-divider>
        
        <h2>Guild Status</h2>

        <table class="utility-table sl-themed">
          <tr>
            <td>Current Guild Status:</td>
            <td class="align-right">
              <sl-tooltip content=${this.guild.status_description}>
                <sl-tag variant=${this.guild.status_tagVariant}>
                  <sl-icon library="material" name=${this.guild.status_icon}></sl-icon>
                  ${this.guild.status_label}
                </sl-tag>
              </sl-tooltip>
            </td>
          </tr>
          
          <tr>
            <td>Current Guild Activation Status:</td>
            <td class="align-right">
              <sl-tooltip content=${this.guild.activationStatus_organizerNotes}>
                <sl-tag variant=${this.guild.activationStatus_tagVariant}>
                  <sl-icon library="material" name=${this.guild.activationStatus_icon}></sl-icon>
                  ${this.guild.activationStatus_label}
                </sl-tag>
              </sl-tooltip>
            </td>
          </tr>

          <tr>
            <td colspan="2">
              ${this.guild.status !== 'draft' ? '' : html`
                The guild is currently in draft mode. Publish the guild in order to make it
                available for interaction.
                <br/><br/>

                <sl-button
                  @click=${this.publish}
                  ?disabled=${this.loading}
                  variant="primary"
                >
                  <sl-icon slot="prefix" library="material" name="offline_bolt"></sl-icon>
                  Publish Guild
                </sl-button>
              `}
            
              ${this.guild.status !== 'live' ? '' : html`
                The guild is currently published and available for interaction.
                Archiving the guild will allow current members to continue interacting,
                but will disable new membership and decrease the visibility of the guild.
                <br/><br/>

                ${this.canArchive ? html`
                  <sl-button
                    @click=${this.archive}
                    ?disabled=${this.loading}
                  >
                    <sl-icon slot="prefix" library="material" name="archive"></sl-icon>
                    Archive Guild
                  </sl-button>
                ` : html`
                  <sl-tooltip content="Only the guild point can archive the guild.">
                    <sl-button disabled>
                      <sl-icon slot="prefix" library="material" name="archive"></sl-icon>
                      Archive Guild
                    </sl-button>
                  </sl-tooltip>
                `}
              `}

              ${this.guild.status !== 'archived' ? '' : html`
                The guild is currently archived. Unarchiving will re-publish the guild and 
                make it active again.
                <br/><br/>

                ${this.canUnarchive ? html`
                  <sl-button
                    @click=${this.unarchive}
                    ?disabled=${this.loading}
                  >
                    <sl-icon slot="prefix" library="material" name="unarchive"></sl-icon>
                    Unarchive Guild
                  </sl-button>
                ` : html`
                  <sl-tooltip content="Only the guild point can unarchive the guild.">
                    <sl-button disabled>
                      <sl-icon slot="prefix" library="material" name="unarchive"></sl-icon>
                      Unarchive Guild
                    </sl-button>
                  </sl-tooltip>
                `}
              `}
            </td>
          </tr>
        </table>
        
        <sl-divider></sl-divider>

        <h2>Guild Point</h2>

        <table class="utility-table">
          <tr>
            <td>Current Guild Point:</td>
            <td class="align-right">
              <q1-member-chip
                .member=${this.currentPoint}
                signed-in
              ></q1-member-chip>
            </td>
          </tr>

          ${!this.newPoint ? '' : html`
            <tr>
              <td>New Point (In Progress):</td>
              <td class="align-right">
                <q1-member-chip
                  .member=${this.newPoint}
                  signed-in
                ></q1-member-chip>
              </td>
            </tr>
          `}

          <tr>
            <td colspan="2">
              ${this.context?.role === 'point' ? html`
                To assign a new guild point, click the button below.
                <br/><br/>
                <sl-button
                  @click=${this.showNewPointDialog}
                >
                  Assign New Guild Point
                </sl-button>
              ` : html`
                The current guild point is able to re-assign the point role
                to any of the other active organizers.
              `}
            </td>
          </tr>
        </table>
      </no-column-wrapper>
    `;
  }

  get detailsFormTemplate() {
    return html`
      <form id="details-form" class="q1 sl-themed-secondary" @submit=${this.handleGuildFormSubmit}>
        <p class="subtitle hero">
          The guild headline is a short description of the guild that should not contain 
          anything confidential as it can sometimes be viewed publicly.
        </p>

        <sl-textarea
          id="headline"
          name="headline"
          label="Guild Headline"
          value=${this.headline}
          @sl-change=${this.handleChange}
          ?disabled=${this.loading}
          maxlength="250"
          rows="3"
          required
        ></sl-textarea>

        <p class="subtitle hero">
          The guild description is a longer description that can be used to describe the guild and 
          its purpose as well as share links that might be helpful to current or potential 
          members and projects. It's visibility is controlled by the guild visibility setting.
        </p>
        
        <q1-md-textarea
          id="description"
          name="description"
          label="Guild Description"
          value=${this.description}
          @change=${this.handleChange}
          maxlength="25000"
          rows="20"
          resize="auto"
          ?disabled=${this.loading}
          required
        ></q1-md-textarea>

        <button-bar class="right-align">
          <sl-button
            variant="primary"
            size="large"
            @click=${this.updateGuild}
            ?disabled=${this.loading}
          >
            <sl-icon slot="prefix" library="material" name="save"></sl-icon>
            Update Guild
          </sl-button>
        </button-bar>

      </form>
    `;
  }
  
  get resourcesFormTemplate() {
    return html`
      <p class="subtitle">
        You can link external tools and resources that support this guild.
        For now, only Slack channels are supported, but more will be added soon.
      </p>
  
      <q1-external-resource-list
        .subjectRef=${this.guild?.ref}
        .externalResources=${this.externalResources}
        .isSignedIn=${this.isSignedIn}
      ></q1-external-resource-list>
    `;
  }  

  get themeFormTemplate() {
    return html`
      <form id="settings-form" class="q1 sl-themed-secondary" @submit=${this.handleGuildFormSubmit}>
        <field-pair>
          <q1-image-upload-input
            id="avatar_newUploadPath"
            name="avatar_newUploadPath"
            upload-folder="guild-avatar"
            current-image-thumbnail=${this.avatar?.mini?.url}
            @change="${this.handleChange}"
            label="Guild Avatar"
            help-text="Image icon representing the guild"
            required
          ></q1-image-upload-input>

          <q1-image-upload-input
            id="banner_newUploadPath"
            name="banner_newUploadPath"
            upload-folder="guild-banner"
            current-image-thumbnail=${this.banner?.mini?.url}
            @change="${this.handleChange}"
            label="Guild Banner"
            help-text="Displayed in the background of the guild header"
          ></q1-image-upload-input>
        </field-pair>

        <field-pair>
          <sl-range
            id="overlayOpacity"
            name="overlayOpacity"
            label="Overlay Opacity"
            value=${this.overlayOpacity}
            @sl-change=${this.handleChange}
            help-text="Opacity of the color overlay over the banner image"
            .tooltipFormatter=${this.rangeTooltipFormatter}
            min="0"
            max="1"
            step="0.05"
          ></sl-range>

          <sl-range
            id="bannerOpacity"
            name="bannerOpacity"
            label="Guild Banner Opacity"
            value=${this.bannerOpacity}
            @sl-change=${this.handleChange}
            help-text="Opacity of the guild banner image"
            .tooltipFormatter=${this.rangeTooltipFormatter}
            min="0"
            max="1"
            step="0.05"
          ></sl-range>
        </field-pair>

        <sl-divider></sl-divider>
        
        <field-pair>
          <input-wrapper>
            <label for="colorPrimary">Primary Guild Color</label>
            <sl-color-picker
              id="colorPrimary"
              name="colorPrimary"
              value=${this.colorPrimary}
              @sl-change=${this.handleChange}
              size="large"
              swatches="#D81D39; #DB3A1A; #DE6616; #E2B013; #9DBC38; #53A84D; 
                #4AAB99; #488BAD; #4864AD; #7D52A3; #A74E9A; #C62F81;"
              required
            ></sl-color-picker>
            <span class="help-text">
              Primary brand color for your guild.
              Used in various places, including for buttons & background gradients.
            </span>
          </input-wrapper>

          <input-wrapper>
            <label for="colorSecondary">Secondary Guild Color</label>
            <sl-color-picker
              id="colorSecondary"
              name="colorSecondary"
              value=${this.colorSecondary}
              @sl-change=${this.handleChange}
              size="large"
              swatches="#D81D39; #DB3A1A; #DE6616; #E2B013; #9DBC38; #53A84D; 
                #4AAB99; #488BAD; #4864AD; #7D52A3; #A74E9A; #C62F81;"
            ></sl-color-picker>
            <span class="help-text">
              Used as a supporting color, including in the overlay on the guild banner image.
            </span>
          </input-wrapper>
        </field-pair>

        <field-pair>
          <sl-radio-group
            id="textColorPrimary"
            name="textColorPrimary"
            label="Text Over Primary Color"
            value=${this.textColorPrimary}
            @sl-change=${this.handleChange}
            help-text="Choose the color that contrasts well when placed over your primary color"
            required
          >
            ${guildTextColorOptions.map((option) => html`
              <sl-radio-button value=${option.value}>
                ${option.label}
              </sl-radio-button>
            `)}
          </sl-radio-group>
          
          <sl-radio-group
            id="textColorSecondary"
            name="textColorSecondary"
            label="Text Over Secondary Color"
            value=${this.textColorSecondary}
            @sl-change=${this.handleChange}
            help-text="Choose the color that contrasts well when placed over your secondary color"
            required
          >
            ${guildTextColorOptions.map((option) => html`
              <sl-radio-button value=${option.value}>
                ${option.label}
              </sl-radio-button>
            `)}
          </sl-radio-group>
        </field-pair>

        <sl-divider></sl-divider>

        <h4>Guild Theme Preview</h4>

        <q1-guild-card
          id="guild-card-preview"
          .guild=${this.guild}
          no-link always-active
        ></q1-guild-card>

        <sl-divider></sl-divider>

        <button-bar class="right-align">
          <sl-button
            id="update-guild-theme-button"
            variant="primary"
            size="large"
            @click=${this.updateGuild}
            ?disabled=${this.loading}
          >
            <sl-icon slot="prefix" library="material" name="save"></sl-icon>
            Update Guild
          </sl-button>
        </button-bar>
      </form>
    `;
  }

  get settingsFormTemplate() {
    return html`
      <form id="settings-form" class="q1 sl-themed-secondary" @submit=${this.handleGuildFormSubmit}>
        <sl-input
          id="name"
          name="name"
          label="Guild Name"
          value=${this.name}
          @sl-change=${this.handleChange}
          maxlength="50"
          ?disabled=${!this.isPoint}
          required
        ></sl-input>
          
        <h3>Guild visibility settings</h3>
        
        <fieldset-panel class="guild-settings">
          <sl-radio-group
            id="visibility"
            name="visibility"
            label="Guild Visibility"
            value=${this.visibility}
            @sl-change=${this.handleChange}
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.isSeeded ? '' : html`
              <span slot="help-text">
                Full visibility options are enabled after guild activation.
              </span>
            `}

            ${this.visibilityRadioButtonsTemplate_withDisabled}
          </sl-radio-group>
          
          <sl-radio-group
            id="memberListVisibility"
            name="memberListVisibility"
            label="Member List Visibility"
            value=${this.memberListVisibility}
            @sl-change=${this.handleChange}"
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.visibilityRadioButtonsTemplate}
          </sl-radio-group>
          
          <sl-radio-group
            id="memberProfileVisibility"
            name="memberProfileVisibility"
            label="Member Profile Visibility"
            value=${this.memberProfileVisibility}
            @sl-change=${this.handleChange}"
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.visibilityRadioButtonsTemplate}
          </sl-radio-group>
          
          <sl-radio-group
            id="projectListVisibility"
            name="projectListVisibility"
            label="Project List Visibility"
            value=${this.projectListVisibility}
            @sl-change=${this.handleChange}"
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.visibilityRadioButtonsTemplate}
          </sl-radio-group>
          
          <sl-radio-group
            id="projectProfileVisibility"
            name="projectProfileVisibility"
            label="Project Profile Visibility"
            value=${this.projectProfileVisibility}
            @sl-change=${this.handleChange}"
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.visibilityRadioButtonsTemplate}
          </sl-radio-group>
        </fieldset-panel>

        <h3>Guild membership settings</h3>

        <p class="subtitle">
          These settings control how new members and projects are able to join the guild.
          Organizers can always invite new members & projects, but these settings allow you to 
          enable open joining or a request-based workflow. Note that guild organizers are always 
          added by the point here in the guild management screen.
        </p>

        <fieldset-panel class="guild-settings">
          <sl-radio-group
            id="supporterJoinability"
            name="supporterJoinability"
            label="Supporter Joining"
            value=${this.supporterJoinability}
            @sl-change=${this.handleChange}
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.joinabilityRadioButtonsTemplate}
          </sl-radio-group>
          
          <sl-radio-group
            id="contributorJoinability"
            name="contributorJoinability"
            label="Contributor Joining"
            value=${this.contributorJoinability}
            @sl-change=${this.handleChange}
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.joinabilityRadioButtonsTemplate}
          </sl-radio-group>
          
          <sl-radio-group
            id="projectJoinability"
            name="projectJoinability"
            label="Project Joining"
            value=${this.projectJoinability}
            @sl-change=${this.handleChange}
            class="label-on-left wrap-button-group"
            ?disabled=${!this.isPoint}
            required
          >
            ${this.joinabilityRadioButtonsTemplate}
          </sl-radio-group>
        </fieldset-panel>
        
        <sl-divider></sl-divider>

        <button-bar class="right-align">
          <sl-button
            variant="primary"
            size="large"
            @click=${this.updateGuild}
            ?disabled=${this.loading || !this.isPoint}
          >
            <sl-icon slot="prefix" library="material" name="save"></sl-icon>
            Update Guild
          </sl-button>
        </button-bar>
      </form>
    `;
  }

  get visibilityRadioButtonsTemplate() {
    return html`
      ${guildVisibilityOptions.map((option) => html`
        <sl-tooltip content=${option.description}>
          <sl-radio-button value=${option.value}>
            <span class="hide-until-bp2">
              <sl-icon
                class="faux-prefix"
                name=${option.icon}
                library=${option.iconLibrary}
              ></sl-icon>
              ${option.label}
            </span>
            <span class="hide-at-bp2">
              <sl-icon name=${option.icon} library=${option.iconLibrary}></sl-icon>
            </span>
          </sl-radio-button>
        </sl-tooltip>
      `)}
    `;
  }

  get visibilityRadioButtonsTemplate_withDisabled() {
    return html`
      ${guildVisibilityOptions.map((option) => html`
        <sl-tooltip content=${option.description}>
          <sl-radio-button
            value=${option.value}
            ?disabled=${!option.availableAt?.includes(this.guild?.activationStatus)}
            class=${this.guild?.visibility === option.value ? 'selected' : ''}
          >
            <span class="hide-until-bp2">
              <sl-icon
                class="faux-prefix"
                name=${option.icon}
                library=${option.iconLibrary}
              ></sl-icon>
              ${option.label}
            </span>
            <span class="hide-at-bp2">
              <sl-icon name=${option.icon} library=${option.iconLibrary}></sl-icon>
            </span>
          </sl-radio-button>
        </sl-tooltip>
      `)}
    `;
  }

  get joinabilityRadioButtonsTemplate() {
    return html`
      ${guildJoinabilityOptions.map((option) => html`
        <sl-tooltip content=${option.description}>
          <sl-radio-button value=${option.value}>
            <span class="hide-until-bp2">
              <sl-icon
                class="faux-prefix"
                name=${option.icon}
                library=${option.iconLibrary}
              ></sl-icon>
              ${option.label}
            </span>
            <span class="hide-at-bp2">
              <sl-icon name=${option.icon} library=${option.iconLibrary}></sl-icon>
            </span>
          </sl-radio-button>
        </sl-tooltip>
      `)}
    `;
  }

  /** Use this for displaying IN a view mode template other than load error. */
  get errorAlertTemplate() {
    if (!this.error) return null;

    return html`
      <sl-alert variant="danger" open>
        <sl-icon library="material" name="report" slot="icon"></sl-icon>
        <strong>An error occurred!</strong><br/>
        ${this.errorMessage}
      </sl-alert>
    `;
  }

  get newPointDialogTemplate() {
    if (this.context.role !== 'point') return null;

    return html`
      <sl-dialog
        id="new-point-dialog"
        label="Assign a New Guild Point"
      >
        ${this.guild.newPoint?.length ? html`
          <p>
            A new guild point assignment is in progress.
            Note that it may take a few minutes for the transition to complete.
            If you see this message for an extended period of time, please contact
            the foundation team for help.
          </p>

          <button-bar slot="footer">
            <sl-button
              @click="${this.closeNewPointDialog}"
            >Close Dialog</sl-button>
          </button-bar>
        ` : html`
          <p>
            To re-assign the guild point, select the new point from the 
            dropdown below.
          </p>
          <p>
            After assigning the new point your role will be changed to organizer.
            The re-assignment will happen right away, the new point will not need to accept.
            It may take a few minutes for the transition to complete, however.
          </p>
          <p>
            <strong>
              WARNING:
              This action cannot be undone. After submitting this form, only the new guild point
              will have the ability to administer the guild.
            </strong>
          </p>

          <form id="new-point-form">
            <sl-select
              id="new-point-select"
              name="newPoint"
              label="New Point"
              placeholder="Select one of the current organizers to assign as the new guild point"
              required
            >
              ${this.organizerOptions.map(({ value, label, isSelf }) => html`
                <sl-option value=${value} ?disabled=${isSelf}>
                  ${label}
                </sl-option>
              `)}
            </sl-select>
          </form>

          <q1-spinner-panel
            label="Processing..."
            ?open=${this.newPoint_processing}
          ></q1-spinner-panel>

          <button-bar slot="footer">
            <sl-button
              @click="${this.closeNewPointDialog}"
            >Cancel</sl-button>
            
            <sl-button variant="danger" @click=${this.saveNewPoint}>
              Re-Assign Guild Point
            </sl-button>
          </button-bar>
        `}
      </sl-dialog>
    `;
  }
}

customElements.define('guild-manage-view', GuildManageView);
